import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Dropdown con multi-seleccion + busqueda + checkboxes.
 *
 * Props:
 *   options       : [{ code, name }]
 *   values        : array de codes seleccionados
 *   onChange(arr) : callback con nuevo array de codes
 *   placeholder   : texto cuando no hay seleccion
 *   label         : opcional, label arriba
 *   searchable    : default true
 *   showCode      : default true (muestra "code - name")
 *   maxHeight     : alto del panel (default 240)
 *   size          : 'sm' | 'md'   (default 'md')
 */
const MultiSelectDropdown = ({
  options = [],
  values = [],
  onChange,
  placeholder = "Seleccionar...",
  label,
  searchable = true,
  showCode = true,
  maxHeight = 240,
  size = "md",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  // Cerrar al click afuera
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Foco en busqueda al abrir
  useEffect(() => {
    if (open && searchable && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open, searchable]);

  const valueSet = useMemo(() => new Set((values || []).map(String)), [values]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        String(o.code).toLowerCase().includes(q) ||
        String(o.name || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  const labelById = useMemo(() => {
    const m = {};
    options.forEach((o) => (m[String(o.code)] = o.name));
    return m;
  }, [options]);

  const toggle = (code) => {
    const k = String(code);
    const next = new Set(valueSet);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    onChange(Array.from(next));
  };

  const selectAllVisible = () => {
    const next = new Set(valueSet);
    filtered.forEach((o) => next.add(String(o.code)));
    onChange(Array.from(next));
  };

  const clearAll = () => onChange([]);

  const summary = useMemo(() => {
    if (!values || values.length === 0) return "";
    if (values.length === 1) {
      const k = String(values[0]);
      return showCode ? `${k} - ${labelById[k] || ""}` : labelById[k] || k;
    }
    return `${values.length} seleccionados`;
  }, [values, labelById, showCode]);

  const cls = size === "sm" ? "form-select-sm" : "";

  return (
    <div ref={ref} className="position-relative" style={{ minWidth: 0 }}>
      {label && <label className="form-label">{label}</label>}

      <button
        type="button"
        disabled={disabled}
        className={`form-select text-start ${cls}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={
          values && values.length > 1
            ? values
                .map((v) => `${v}${labelById[v] ? " - " + labelById[v] : ""}`)
                .join(", ")
            : ""
        }
      >
        {summary || (
          <span className="text-muted">{placeholder}</span>
        )}
      </button>

      {open && (
        <div
          className="border rounded shadow-sm bg-white position-absolute mt-1 p-2"
          style={{ zIndex: 1050, width: "100%", minWidth: 260 }}
        >
          {searchable && (
            <input
              ref={inputRef}
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Buscar..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}

          <div className="d-flex justify-content-between mb-1">
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={selectAllVisible}
            >
              Marcar visibles
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-danger"
              onClick={clearAll}
            >
              Limpiar
            </button>
          </div>

          <div style={{ maxHeight, overflowY: "auto" }}>
            {filtered.length === 0 && (
              <div className="text-muted small px-2 py-3 text-center">
                Sin resultados.
              </div>
            )}
            {filtered.map((o) => {
              const k = String(o.code);
              const checked = valueSet.has(k);
              return (
                <label
                  key={k}
                  className="d-flex align-items-center gap-2 px-2 py-1 rounded user-select-none"
                  style={{
                    cursor: "pointer",
                    background: checked ? "#eef4ff" : "transparent",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = checked
                      ? "#dde9ff"
                      : "#f3f5f7")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = checked
                      ? "#eef4ff"
                      : "transparent")
                  }
                >
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={checked}
                    onChange={() => toggle(k)}
                  />
                  <span className="small" style={{ lineHeight: 1.2 }}>
                    {showCode ? (
                      <>
                        <strong>{o.code}</strong>
                        {o.name ? ` - ${o.name}` : ""}
                      </>
                    ) : (
                      o.name || o.code
                    )}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="d-flex justify-content-end mt-2">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => setOpen(false)}
            >
              Listo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
